/**
 * ClassName Utility (cn)
 * 
 * Merges className strings intelligently
 * Handles conditional classes and removes conflicts
 * 
 * Based on: clsx + tailwind-merge pattern
 */

/**
 * Simple clsx implementation
 * Combines class names from various input types
 */
function clsx(...inputs) {
  const classes = [];

  for (const input of inputs) {
    if (!input) continue;

    const type = typeof input;

    if (type === 'string' || type === 'number') {
      classes.push(input);
    } else if (Array.isArray(input)) {
      if (input.length) {
        const inner = clsx(...input);
        if (inner) {
          classes.push(inner);
        }
      }
    } else if (type === 'object') {
      for (const key in input) {
        if (input[key]) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(' ');
}

/**
 * Simple Tailwind merge
 * Removes conflicting Tailwind classes
 * 
 * Example: cn('px-2', 'px-4') => 'px-4' (last one wins)
 */
function twMerge(...classes) {
  // Join all classes
  const classString = classes.filter(Boolean).join(' ');
  
  // Split into individual classes
  const classList = classString.split(' ').filter(Boolean);
  
  // Track seen prefixes to handle conflicts
  const classMap = new Map();
  
  // Process each class
  for (const cls of classList) {
    // Extract prefix (e.g., 'px' from 'px-4', 'bg' from 'bg-blue-500')
    const match = cls.match(/^([a-z]+(?:-[a-z]+)?)-/);
    
    if (match) {
      const prefix = match[1];
      // Store class with its prefix (last one wins)
      classMap.set(prefix, cls);
    } else {
      // No prefix conflict possible, store with unique key
      classMap.set(cls, cls);
    }
  }
  
  // Return merged classes
  return Array.from(classMap.values()).join(' ');
}

/**
 * Main cn utility
 * Combines clsx functionality with Tailwind merge
 * 
 * @param {...(string|Object|Array)} inputs - Class values to merge
 * @returns {string} Merged class string
 * 
 * @example
 * cn('px-2 py-1', 'px-4') // => 'py-1 px-4'
 * cn('text-center', { 'font-bold': true, 'text-blue': false }) // => 'text-center font-bold'
 * cn(['flex', 'items-center'], undefined, 'gap-2') // => 'flex items-center gap-2'
 */
export function cn(...inputs) {
  return twMerge(clsx(...inputs));
}

// Default export
export default cn;